import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useApi } from '../hooks/useApi';
import ConfirmModal from './ConfirmModal';
import SearchableSelect from './SearchableSelect';
import SortIcon from './SortIcon';
import { useSortConfig } from '../hooks/useSortConfig';
import { CAP } from '../utils/permissions';
import { usePermissions, ReadOnlyNotice } from '../utils/PermissionContext';

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const VisibleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-green-600">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const InvisibleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
  </svg>
);


const SpreadsheetIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="shadow-sm rounded-md">
    <rect width="24" height="24" rx="4" fill="#0F9D58" />
    <rect x="7.5" y="4" width="2.5" height="16" fill="white" />
    <rect x="4" y="8" width="16" height="2.5" fill="white" />
  </svg>
);

const COLLECTION_CONFIG = {
  workspaces: {
    label: "Workspace products",
    apiEndpoint: "/api/workspaces",
    fields: [
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'afas_artikelcode', label: 'AFAS Artikel Code', type: 'text' },
      { key: 'price', label: 'Annual Price (€)', type: 'number' },
      { key: 'flex_price', label: 'Flex plan price', type: 'number' },
      { key: 'related_licenses_sku', label: 'Related SKU', type: 'text', hideInTable: true },
      { key: 'order', label: 'Order', type: 'number' },
      { key: 'visible', label: 'Visible', type: 'boolean' }
    ]
  },
  extraProducts: {
    label: "Service Packages & Product Catalog",
    apiEndpoint: ["/api/service-packages", "/api/product-catalog"],
    fields: [
      { key: '_collectionType', label: 'Type', type: 'select', hideInTable: true, options: [
        { value: '/api/service-packages', label: 'Service Package' },
        { value: '/api/product-catalog', label: 'Product Catalog' }
      ]},
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'afas_artikelcode', label: 'AFAS Artikel Code', type: 'text' },
      { key: 'price', label: 'Price (€)', type: 'number' },
      { key: 'hours', label: 'Hours', type: 'number', hideInTable: true },
      { key: 'category', label: 'Category', type: 'text', hideInTable: true },
      { key: 'description', label: 'Description', type: 'textarea', hideInTable: true },
      { key: 'related_licenses_sku', label: 'Related SKU', type: 'text', hideInTable: true },
      { key: 'order', label: 'Order', type: 'number' },
      { key: 'icon', label: 'Icon Key', type: 'text', hideInTable: true },
      { key: 'visible', label: 'Visible', type: 'boolean' }
    ]
  }
};


const WorkspacesTableSkeleton = ({ currentFields, t }) => {
  return (
    <div className="bg-white shadow rounded-lg overflow-x-auto animate-pulse">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-primary text-white opacity-85">
          <tr>
            {currentFields.filter(f => !f.hideInTable).map(field => (
              <th
                key={field.key}
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
              >
                {t[field.key] || field.label}
              </th>
            ))}
            <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">
              {t.actions || 'Actions'}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {[1, 2, 3, 4, 5].map((rowIdx) => (
            <tr key={rowIdx}>
              {currentFields.filter(f => !f.hideInTable).map(field => (
                <td key={field.key} className="px-6 py-4 whitespace-nowrap">
                  {field.type === 'boolean' ? (
                    <div className="h-6 w-6 bg-gray-200 rounded"></div>
                  ) : (
                    <div className={`h-4 bg-gray-200 rounded ${field.key === 'name' ? 'w-48' :
                      field.key === 'afas_artikelcode' ? 'w-24' :
                        field.key === 'price' ? 'w-16' :
                          'w-32'
                      }`}></div>
                  )}
                </td>
              ))}
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <div className="flex justify-end gap-3">
                  <div className="h-6 w-6 bg-gray-200 rounded"></div>
                  <div className="h-6 w-6 bg-gray-200 rounded"></div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Workspaces = ({ t }) => {
  const { can } = usePermissions();
  const canWrite = can(CAP.ADMIN_WRITE);
  const { apiRequest } = useApi(localStorage.getItem('googleIdToken'), () => window.location.reload());

  const [activeTab, setActiveTab] = useState('workspaces');
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState(null); // item id pending delete confirmation

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [sortConfig, requestSort, setSortConfig] = useSortConfig(null, ['ascending', 'descending']);
  const [afasArticles, setAfasArticles] = useState([]);

  useEffect(() => {
    const fetchAfasArticles = async () => {
      try {
        const result = await apiRequest('/api/afas-articles', { method: 'GET' });
        setAfasArticles(result || []);
      } catch (error) {
        console.error("Error fetching afas articles:", error);
      }
    };
    fetchAfasArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSortConfig(null);
    fetchCollectionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchCollectionData = async () => {
    setIsLoading(true);
    try {
      const endpoint = COLLECTION_CONFIG[activeTab].apiEndpoint;
      if (Array.isArray(endpoint)) {
        const results = await Promise.all(endpoint.map(ep => apiRequest(ep, { method: 'GET' })));
        const combinedData = [];
        results.forEach((res, index) => {
          const ep = endpoint[index];
          const arrayData = Array.isArray(res) ? res : (res.data || []);
          arrayData.forEach(item => combinedData.push({ ...item, _collectionType: ep }));
        });
        setData(combinedData);
      } else {
        const result = await apiRequest(endpoint, { method: 'GET' });
        const arrayData = Array.isArray(result) ? result : (result.data || []);
        setData(arrayData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setIsDeleting(true);
    const config = COLLECTION_CONFIG[activeTab];
    const itemToDelete = data.find(item => item.id === id);
    const targetEndpoint = itemToDelete?._collectionType || (Array.isArray(config.apiEndpoint) ? config.apiEndpoint[0] : config.apiEndpoint);
    try {
      await apiRequest(`${targetEndpoint}?id=${id}`, { method: 'DELETE' });
      toast.success("Item deleted successfully.");
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('dashboard_initial_data_')) sessionStorage.removeItem(key);
      });
      fetchCollectionData();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Failed to delete item.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingItem({ _collectionType: Array.isArray(COLLECTION_CONFIG[activeTab].apiEndpoint) ? COLLECTION_CONFIG[activeTab].apiEndpoint[0] : '' });
    setIsModalOpen(true);
  };

  const handleToggleVisibility = async (item) => {
    const isWorkspaces = activeTab === 'workspaces';
    if (isWorkspaces && item.visible === false) {
      const isPriceZero = item.price === 0 || Number(item.price) === 0 || !item.price;
      const isAfasEmpty = !item.afas_artikelcode || item.afas_artikelcode.toString().trim() === '';
      if (isPriceZero || isAfasEmpty) {
        let reason = "";
        if (isPriceZero && isAfasEmpty) {
          reason = t.cannot_make_visible_both_reason || "price is €0 and AFAS article code is empty";
        } else if (isPriceZero) {
          reason = t.cannot_make_visible_price_reason || "price is €0";
        } else {
          reason = t.cannot_make_visible_afas_reason || "AFAS article code is empty";
        }
        toast.error(`${t.cannot_make_visible || "Cannot make visible"}: ${reason}`);
        return;
      }
    }

    setIsSaving(true);
    const config = COLLECTION_CONFIG[activeTab];
    const targetEndpoint = item._collectionType || (Array.isArray(config.apiEndpoint) ? config.apiEndpoint[0] : config.apiEndpoint);
    const updatedItem = { ...item, visible: item.visible === false ? true : false };
    delete updatedItem._collectionType;
    try {
      await apiRequest(targetEndpoint, {
        method: 'POST',
        body: JSON.stringify(updatedItem)
      });
      toast.success("Visibility updated!");
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('dashboard_initial_data_')) sessionStorage.removeItem(key);
      });
      fetchCollectionData();
    } catch (error) {
      console.error("Error updating visibility:", error);
      toast.error("Failed to update visibility.");
    } finally {
      setIsSaving(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const config = COLLECTION_CONFIG[activeTab];

    // Ensure item conforms to visibility rules
    let itemToSave = { ...editingItem };
    if (activeTab === 'workspaces') {
      const isPriceZero = itemToSave.price === 0 || Number(itemToSave.price) === 0 || !itemToSave.price;
      const isAfasEmpty = !itemToSave.afas_artikelcode || itemToSave.afas_artikelcode.toString().trim() === '';
      if (isPriceZero || isAfasEmpty) {
        itemToSave.visible = false;
      }
    }

    const targetEndpoint = itemToSave._collectionType || (Array.isArray(config.apiEndpoint) ? config.apiEndpoint[0] : config.apiEndpoint);
    delete itemToSave._collectionType;

    try {
      await apiRequest(targetEndpoint, {
        method: 'POST',
        body: JSON.stringify(itemToSave)
      });
      toast.success("Saved successfully!");
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('dashboard_initial_data_')) sessionStorage.removeItem(key);
      });
      closeModal();
      fetchCollectionData();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save. Check console.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e, fieldKey, type) => {
    let value = e.target.value;
    if (type === 'number') value = parseFloat(value);
    setEditingItem(prev => ({ ...prev, [fieldKey]: value }));
  };

  const sortedData = React.useMemo(() => {
    let sortableItems = [...data];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];

        // Handle case-insensitive string comparison
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (valA > valB) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    } else {
      sortableItems.sort((a, b) => {
        const orderA = a.order !== undefined && a.order !== null && a.order !== '' ? Number(a.order) : Infinity;
        const orderB = b.order !== undefined && b.order !== null && b.order !== '' ? Number(b.order) : Infinity;
        return orderA - orderB;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const currentFields = COLLECTION_CONFIG[activeTab].fields;

  return (
    <div className="p-4 sm:p-8">
      <ReadOnlyNotice t={t} />
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            {t.admin_webshop}
          </h1>
          <a
            href="https://docs.google.com/spreadsheets/d/1UhBrETJk1t50L-vk1-bpoOMUAJmPzZd9UPHBYmA6rMI"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:scale-105 transition-transform"
            title="Open Google Spreadsheet"
          >
            <SpreadsheetIcon />
          </a>
        </div>
        <div className="flex items-center gap-3">
          {canWrite && <button onClick={handleAddNew} className="bg-secondary text-white px-4 py-2 rounded hover:bg-secondary-hover shadow flex items-center gap-2">
            <span>+ {t.add_new_item}</span>
          </button>}
        </div>
      </div>

      {/* --- TABS --- */}
      <div className="flex border-b border-gray-200 mb-6">
        {Object.keys(COLLECTION_CONFIG).map((key) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-6 py-3 font-medium text-sm focus:outline-none transition-colors ${activeTab === key ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            {COLLECTION_CONFIG[key].label}
          </button>
        ))}
      </div>

      {/* --- TABLE --- */}
      {isLoading ? (
        <WorkspacesTableSkeleton currentFields={currentFields} t={t} />
      ) : (
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-primary text-white">
              <tr>
                {currentFields.filter(f => !f.hideInTable).map(field => (
                  <th
                    key={field.key}
                    onClick={() => requestSort(field.key)}
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors group"
                  >
                    <div className="flex items-center gap-1">
                      {t[field.key] || field.label}
                      <span className={sortConfig?.key === field.key ? 'text-white' : 'text-purple-300 opacity-0 group-hover:opacity-100 transition-opacity'}>
                        <SortIcon direction={sortConfig?.key === field.key ? sortConfig.direction : null} />
                      </span>
                    </div>
                  </th>
                ))}
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">{t.actions}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedData.map((item, index) => (
                <tr key={item.id || index} className={`hover:bg-gray-50 ${item.visible === false ? 'bg-gray-100 text-gray-500' : ''}`}>
                  {currentFields.filter(f => !f.hideInTable).map(field => (
                    <td key={field.key} className={`px-6 py-4 whitespace-nowrap text-sm ${item.visible === false ? 'text-gray-400' : 'text-gray-900'}`}>
                      {field.type === 'boolean' ? (
                        // Also the visibility indicator, so it stays as an
                        // inert icon rather than vanishing.
                        <button
                          onClick={() => handleToggleVisibility(item)}
                          disabled={!canWrite || isSaving}
                          title={!canWrite ? undefined : item[field.key] !== false ? "Click to hide" : "Click to show"}
                          className={`p-1 rounded transition-colors flex items-center ${!canWrite ? 'cursor-default' : isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                        >
                          {item[field.key] !== false ? <VisibleIcon /> : <InvisibleIcon />}
                        </button>
                      ) : field.key === 'afas_artikelcode' ? (
                        <div className="flex items-center gap-1.5">
                          <span>{item[field.key]}</span>
                          {(!item[field.key] || item[field.key].toString().trim() === '') ? (
                            <span
                              className="inline-flex items-center justify-center bg-amber-100 text-amber-800 border border-amber-200 rounded-full h-5 w-5 font-extrabold text-xs cursor-help select-none animate-pulse shrink-0"
                              title={t.afas_artikelcode_empty || "AFAS artikelcode is leeg."}
                            >
                              !
                            </span>
                          ) : (afasArticles.length > 0 && !afasArticles.some(a => a.ItemCode === item[field.key])) ? (
                            <span
                              className="inline-flex items-center justify-center bg-red-100 text-red-800 border border-red-200 rounded-full h-5 w-5 font-extrabold text-xs cursor-help select-none animate-pulse shrink-0"
                              title={t.afas_artikelcode_not_found || "AFAS artikelcode is niet gevonden."}
                            >
                              !
                            </span>
                          ) : null}
                        </div>
                      ) : field.key === 'price' ? (
                        <div className="flex items-center gap-1.5">
                          <span>{item[field.key]}</span>
                          {(item[field.key] === 0 || Number(item[field.key]) === 0) && (
                            <span
                              className="inline-flex items-center justify-center bg-amber-100 text-amber-800 border border-amber-200 rounded-full h-5 w-5 font-extrabold text-xs cursor-help select-none animate-pulse shrink-0"
                              title={t.contact_admin_to_update_price || "Neem contact op met de beheerder om de prijs bij te werken."}
                            >
                              !
                            </span>
                          )}
                        </div>
                      ) : (
                        item[field.key]
                      )}
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-3">
                      {canWrite && <>
                      <button
                        onClick={() => handleEdit(item)}
                        disabled={isDeleting}
                        title="Edit"
                        className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-colors"
                      >
                        <EditIcon />
                      </button>
                      <button
                        onClick={() => setDeleteId(item.id)}
                        disabled={isDeleting}
                        title="Delete"
                        className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                      >
                        <DeleteIcon />
                      </button>
                      </>}
                    </div>
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={currentFields.filter(f => !f.hideInTable).length + 1} className="px-6 py-4 text-center text-gray-500">
                    {t.no_item_found}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL FOR CREATE / EDIT */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h2 className="text-xl font-bold mb-4">{editingItem?.id ? t.edit_item : t.add_new_item}</h2>
            <form onSubmit={handleSave}>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div className="bg-fuchsia-50 border-l-4 border-primary p-4 mb-2">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-primary" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-primary">
                        {t.sku_help_text}
                        <a
                          href="https://developers.google.com/workspace/admin/licensing/v1/how-tos/products"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold underline hover:text-primary-hover"
                        >
                          {t.google_workspace_docs}
                        </a> &&nbsp;
                        <a
                          href="https://developers.google.com/workspace/admin/reseller/v1/how-tos/products"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold underline hover:text-primary-hover"
                        >
                          {t.google_reseller_docs}
                        </a>.
                      </p>
                    </div>
                  </div>
                </div>
                {currentFields.map((field) => (
                  <div key={field.key}>
                    {field.type !== 'boolean' && (
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">{t[field.key] || field.label}</label>
                        {field.key === 'afas_artikelcode' && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); window.dispatchEvent(new Event('open-afas-pip')); }}
                            className="text-xs text-primary hover:text-primary-hover flex items-center gap-1 font-medium bg-fuchsia-50 px-2 py-0.5 rounded border border-primary transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                            AFAS Article Code
                          </button>
                        )}
                      </div>
                    )}
                    {field.type === 'textarea' ? (
                      <textarea
                        className="w-full border rounded p-2 focus:ring-2 focus:ring-primary outline-none"
                        rows="3" disabled={isSaving} value={editingItem[field.key] || ''}
                        onChange={(e) => handleInputChange(e, field.key, field.type)}
                      />
                    ) : field.type === 'boolean' ? (
                      <div>
                        <label className="flex items-center cursor-pointer mt-4 mb-2">
                          <input
                            type="checkbox"
                            className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary disabled:opacity-50"
                            checked={activeTab === 'workspaces' && (editingItem.price === 0 || Number(editingItem.price) === 0 || !editingItem.price || !editingItem.afas_artikelcode || editingItem.afas_artikelcode.toString().trim() === '') ? false : (editingItem[field.key] !== false)}
                            disabled={isSaving || (activeTab === 'workspaces' && (editingItem.price === 0 || Number(editingItem.price) === 0 || !editingItem.price || !editingItem.afas_artikelcode || editingItem.afas_artikelcode.toString().trim() === ''))}
                            onChange={(e) => handleInputChange({ target: { value: e.target.checked } }, field.key, field.type)}
                          />
                          <span className="ml-2 text-sm text-gray-700">{t[field.key] || field.label}</span>
                        </label>
                        {activeTab === 'workspaces' && (editingItem.price === 0 || Number(editingItem.price) === 0 || !editingItem.price || !editingItem.afas_artikelcode || editingItem.afas_artikelcode.toString().trim() === '') && (
                          <p className="text-xs text-red-500 mt-1 font-medium">
                            {t.cannot_be_visible_warning || "Kan niet zichtbaar zijn omdat de prijs €0 is of de AFAS artikelcode leeg is."}
                          </p>
                        )}
                      </div>
                    ) : field.key === 'afas_artikelcode' ? (
                      <SearchableSelect
                        options={afasArticles.map(article => ({
                          value: article.ItemCode,
                          label: `${article.ItemCode}${article.ItemDescription ? ` - ${article.ItemDescription}` : ''}`
                        }))}
                        value={editingItem[field.key] || ''}
                        onChange={(val) => handleInputChange({ target: { value: val } }, field.key, field.type)}
                        disabled={isSaving}
                        placeholder={t.select_afas_article || 'Select AFAS Article'}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        className="w-full border rounded p-2 focus:ring-2 focus:ring-primary outline-none bg-white"
                        disabled={isSaving || (editingItem.id && field.key === '_collectionType')}
                        value={editingItem[field.key] || ''}
                        onChange={(e) => handleInputChange(e, field.key, field.type)}
                      >
                        <option value="" disabled>{t.select_option || 'Select...'}</option>
                        {field.options.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        className="w-full border rounded p-2 focus:ring-2 focus:ring-primary outline-none"
                        step={field.type === 'number' ? "0.01" : undefined}
                        disabled={isSaving} value={editingItem[field.key] || ''}
                        onChange={(e) => handleInputChange(e, field.key, field.type)}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={closeModal} disabled={isSaving} className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100 disabled:opacity-50">{t.cancel}</button>
                <button type="submit" disabled={isSaving} className="px-4 py-2 bg-secondary text-white rounded hover:bg-secondary-hover disabled:bg-gray-400 flex items-center gap-2">
                  {isSaving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                  {isSaving ? t.saving : t.save_changes}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title={t.delete || 'Delete'}
        message={t.delete_item_confirmation}
        confirmLabel={t.delete || 'Delete'}
        cancelLabel={t.cancel}
        tone="danger"
        busy={isDeleting}
        onCancel={() => setDeleteId(null)}
        onConfirm={async () => {
          const id = deleteId;
          await handleDelete(id);
          setDeleteId(null);
        }}
      />
    </div>
  );
};

export default Workspaces;